import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { ApiRequestService } from '../shared/api-request.service';
import { UtilsService } from '../shared/utils.service';
import { CustomDataSource } from '../utils/custom-data-source';
import { FolderModel } from '../shared/folder.model';
import { FolderService } from '../shared/folder.service';
import { MatDialog } from '@angular/material/dialog';
import { SiteModel } from '../models/site.model';
import { AccountDocsLayoutComponent } from '../account/account-docs/account-docs-layout/account-docs-layout.component';
import { TemplateDocsViewComponent } from './template-docs-view/template-docs-view.component';
import { RBACService } from '../shared/rbac.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-template-docs',
  templateUrl: './template-docs.component.html',
  styleUrls: ['./template-docs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TemplateDocsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new LibraryDataSource(this.app, this.api);
  displayedColumns: string[] = [];

  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreate: boolean = false;
  canView: boolean = false;
  canViewAndEdit: boolean = false;
  canViewAndDelete: boolean = false;

  constructor(
    public utils: UtilsService,
    private folderService: FolderService,
    private app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // Resolve the permission flags first so they are always set, even when the early return below fires.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canViewAndEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canViewAndDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check if user has ANY permission for template documents (view OR create OR edit OR delete)
    const hasAnyPermission = this.canView || this.canCreate; // OR logic: user needs ANY of these permissions

    if (!hasAnyPermission) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access template documents.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // Build displayed columns - only include select column if user has delete permission
    this.displayedColumns = [
      ...(this.canViewAndDelete ? ['select'] : []),
      'id',
      'name',
      'media',
      'links',
      'sites',
      'expiry',
      'created_at',
      'buttons'
    ];

    // Only load data if user has view permission
    if (this.canView) {
      this.dataSource.getData(false);
    }
  }

  add() {
    // Check permission to create template documents (standalone - no view required)
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create template documents.'
      );
      return;
    }

    this.openDialog({ template: true } as FolderModel);
  }

  view(folder: FolderModel) {
    // Layer 2 guard: the row button is inside the view-gated table, but the method can also be
    // called programmatically, so it enforces the permission itself.
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view template documents.'
      );
      return;
    }

    this.dialog
      .open(TemplateDocsViewComponent, {
        width: '750px',
        data: {
          folder: folder,
          is_training_doc: false,
          permissions: {
            feature: RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            canCreate: false,
            canEdit: false,
            canDelete: false
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData(true);
      });
  }

  edit(folder: FolderModel) {
    // Check permission to edit template documents (requires view + edit)
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit template documents.'
      );
      return;
    }

    this.openDialog(folder);
  }

  private openDialog(folder: FolderModel) {
    this.dialog
      .open(AccountDocsLayoutComponent, {
        width: '1200px',
        data: {
          folder: folder,
          is_training_doc: false,
          show_document_types_list_for: 'account',
          show_document_visibility: false,
          permissions: {
            feature: RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            canCreate: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
                  RBACService.RBAC_A.A_CREATE
                )
              ],
              true
            ),
            canEdit: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
                  RBACService.RBAC_A.A_EDIT
                )
              ],
              true
            ),
            canDelete: this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
                  RBACService.RBAC_A.A_VIEW
                ),
                RBACService.getPermission(
                  RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
                  RBACService.RBAC_A.A_DELETE
                )
              ],
              true
            )
          }
        }
      })
      .afterClosed()
      .subscribe((result) => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  onRemove(folder: FolderModel) {
    this.utils.showModal(
      'Remove Folder',
      `Are you sure you want to remove "${folder.name}"?`,
      () => {
        this.remove([folder.id]);
      }
    );
  }

  onRemoveSelected() {
    this.utils.showModal(
      'Remove Folders',
      `Are you sure you want to remove ${
        this.dataSource.selection.selected.length
      } Folders?`,
      () => {
        this.remove(this.dataSource.selection.selected);
      }
    );
  }

  private remove(ids: number[]) {
    // Check permission to delete template documents (requires view + delete)
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete template documents.'
      );
      return;
    }

    this.folderService.delete(ids).subscribe(() => {
      this.utils.showToast('Removed Folder(s)');
      this.dataSource.selection.clear();
      this.dataSource.getData(true);
    });
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC columns by their corresponding date columns.
      if (this.sort.active == 'created_at_UTC') {
        this.dataSource.sort_by = 'created_at';
      }

      this.dataSource.getData(false);
    });
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Format the text display for associated sites.
   * Examples:
   *  - Head Office
   *  - 0 Sites
   *  - 1 Site (If name is not accessible otherwise the name of the site)
   *  - 2 Sites
   *  - Etc.
   * @param form
   */
  formatSitesListColumnText(document: FolderModel) {
    // Check if we have one user.
    if (document.sites_count == 1) {
      // Check if we have details of one user.
      if (document.sites[0]) {
        return document.sites[0].name;
      }
      // Respond with a generic 1 User response.
      return (
        document.sites_count +
        ' ' +
        this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site')
      );
    }
    // Respond with the users count.
    return (
      document.sites_count +
      ' ' +
      this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites')
    );
  }

  /**
   * The tooltip text is only used if there is more than 1 site.
   * @param form
   */
  formatSitesListColumnTooltip(document: FolderModel) {
    if (document.sites_count > 1) {
      const site_names: string[] = document.sites.map((site: SiteModel) => {
        return site.name;
      });
      return site_names.join(`\n`);
    }
    return '';
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}

export class LibraryDataSource extends CustomDataSource {
  sort_by = 'name';
  sort_order = 'asc';

  getData(resetOffset = false) {
    this.getDataFromLaravelAPI(
      `folders?template=true&eager_load=true`,
      resetOffset
    );
  }
}
